<template>
  <transition name="fade">
    <div
      v-show="state.open"
      class="cart-drawer"
    >
      <div
        class="cart-drawer__overlay"
        @click="handleClose"
      />

      <transition name="slide-left">
        <div
          v-if="state.open"
          aria-describedby="a11y-cart-drawer-title"
          class="cart-drawer__drawer"
          role="dialog"
          @focusout="handleFocusOut"
        >
          <header class="cart-drawer__header">
            <h2
              id="a11y-cart-drawer-title"
              class="cart-drawer__title"
              v-text="drawerTitle"
            />

            <button
              class="cart-drawer__close"
              :aria-label="strings.close"
              @click="handleClose"
            >
              <icon-close />
            </button>
          </header>

          <form
            v-if="cartData.item_count > 0"
            class="cart-drawer__items"
            :class="{ 'is-locked': state.locked }"
          >
            <cart-drawer-item
              v-for="item in cartData.items"
              :key="item.key"
              :disabled="state.locked"
              :item="item"
              @lock-drawer="handleLockChange"
            />
          </form>

          <template v-else>
            <div class="cart-drawer__empty">
              <p
                class="cart-drawer__empty-message"
                v-text="strings.emptyMessage"
              />
            </div>

            <div
              v-if="bloomreach?.enabled"
              class="cart-drawer__ymal"
              data-mint-component="bloomreach-recommendations"
            >
              <heading
                v-if="bloomreach.title"
                class="cart-drawer__ymal-heading"
                type="h3"
                :text="bloomreach.title"
              />

              <bloomreach-recommendations
                context="cart-popup"
                :recommendation-id="bloomreach.recommendationId"
                :recommendation-size="4"
                :section-id="sectionId"
                :tracking-parameters="trackingParameters"
              />
            </div>
          </template>

          <ul
            v-if="getUspList?.items?.length"
            class="cart-drawer__usp-list"
          >
            <li
              v-for="(usp, index) in getUspList.items"
              :key="index"
              class="cart-drawer__usp"
            >
              <img
                alt=""
                class="cart-drawer__usp-icon"
                :src="usp.icon"
              >
              <span
                class="cart-drawer__usp-label"
                v-html="usp.label"
              />
            </li>
          </ul>

          <footer
            v-if="cartData.item_count > 0"
            class="cart-drawer__footer"
          >
            <p class="cart-drawer__total">
              <span v-text="strings.total" />
              <span v-text="getCartTotal" />
            </p>

            <btn
              class="cart-drawer__button button--primary button--block"
              :label="strings.viewCart"
              :url="viewCartUrl"
            />

            <btn
              class="cart-drawer__button button--black button--block"
              :label="strings.checkout"
              :url="checkoutUrl"
            />
          </footer>
        </div>
      </transition>
    </div>
  </transition>
</template>

<script>

/**
 * Section: Cart drawer
 * ------------------------------------------------------------------------------
 * - Overlay showing the contents of the user's cart.
 * @param {Object} [bloomreach] - Bloomreach settings object
 * @param {Boolean} [bloomreach.enabled] - Whether the Bloomreach carousel
 *   should be enabled.
 * @param {Object} [bloomreach.title] - Title for the Bloomreach carousel.
 * @param {Object} [bloomreach.recommendationId] - Bloomreach recommendation ID.
 * @param {Object} [bloomreach.trackingSectionName] - Tracking name for the
 *   section.
 * @param {String} sectionId - Shopify section ID.
 * @param {Object} uspList - USP list sourced from metaobjects.
 */
import { nextTick } from 'vue'

import { mapState } from 'pinia'

import { useCartStore } from '~stores/cart'

import classes from '~/helpers/classes'
import { formatLocalizedPrice } from '~/helpers/format'
import { importComponent } from '~/core/mint/helpers'
import { generateCheckoutPermalink } from '~/helpers/utils'

import Btn from '~components/btn/btn'
import CartDrawerItem from '~components/cart-drawer-item/cart-drawer-item'
import IconClose from '~icons/misc/close.svg'

export default {
  name: 'CartDrawer',

  components: {
    BloomreachRecommendations: importComponent('bloomreach-recommendations'),
    Btn,
    CartDrawerItem,
    Heading: importComponent('heading'),
    IconClose,
  },

  props: {
    bloomreach: {
      type: Object,
      default: () => {},
    },
    sectionId: {
      type: String,
      default: '',
    },
    uspList: {
      type: [Boolean, Object],
      default: () => {},
    },
  },

  data() {
    return {
      lastActiveElement: null,
      state: {
        locked: false,
        open: false,
      },
      strings: {
        checkout: mint.Strings('cart.drawer.checkout'),
        close: mint.Strings('cart.drawer.close'),
        emptyMessage: mint.Strings('cart.drawer.empty_message'),
        total: mint.Strings('cart.drawer.total'),
        viewCart: mint.Strings('cart.drawer.view_cart'),
      },
    }
  },

  computed: {

    /**
     * Map the cart data.
     */
    ...mapState(useCartStore, ['cartData']),

    /**
     * Get the URL for the checkout link.
     * - Goes directly to checkout
     * @returns {String}
     */
    checkoutUrl() {
      const customerDetails = {
        email: mint.customer.email,
        firstName: mint.customer.firstName,
        lastName: mint.customer.lastName,
      }

      if (!customerDetails.email) {
        return mint.urls.checkout
      }

      return generateCheckoutPermalink(customerDetails)
    },

    /**
     * Get the locale string for the cart drawer title, and insert the current
     * item count.
     */
    drawerTitle() {
      return mint.Strings('cart.drawer.title', {
        replace: { count: this.cartData.item_count },
      })
    },

    /**
     * Format the cart total in the user's currency.
     * @returns {String}
     */
    getCartTotal() {
      return formatLocalizedPrice(this.cartData.total_price)
    },

    /**
     * Get the appropriate USP list for the user's locale.
     * @returns {Array}
     */
    getUspList() {
      if (!this.uspList || !this.uspList?.items) {
        return []
      }

      return this.uspList
    },

    /**
     * Format Bloomreach tracking parameters.
     * @returns {String}
     */
    trackingParameters() {
      return { section: this.bloomreach?.trackingSectionName }
    },

    /**
     * Get the cart URL from the window.mint object.
     * @returns {String}
     */
    viewCartUrl() {
      return window.mint.routes.cart.url
    },
  },

  mounted () {
    this.setEventListeners()
  },

  methods: {

    /**
     * Handle closing the cart drawer.
     * - Unlock the body element
     * - Update state
     * - Return focus to the last active element
     */
    close() {
      if (!this.state.open) {
        return
      }

      if (typeof window.zE === 'function') {
        window.zE('webWidget', 'show')
      }

      document.body.classList.remove(classes.locked)
      this.state.open = false

      /**
       * Return focus to the last active element if keyboard navigation has
       * been detected.
       */
      if (
        this.lastActiveElement &&
        mint.accessibility.keyboardNavigation
      ) {
        this.lastActiveElement.focus()
      }
    },

    /**
     * Dispatch a close event.
     */
    handleClose() {
      mint.EventBus.dispatch('cartDrawer:close')
    },

    /**
     * Close the drawer on focus out, if keyboard navigation is active.
     */
    handleFocusOut() {
      if (!mint.accessibility.keyboardNavigation) {
        return
      }

      this.handleClose()
    },

    /**
     * Handle lock state updates.
     * @param {Boolean} lock
     */
    handleLockChange(lock = false) {
      this.state.locked = lock
    },

    /**
     * Handle events when a section is selected in the theme editor.
     * @param {Object} event - Event data object.
     * @param {Boolean} isSelected - Whether the section has been selected or
     *   deselected. This maps to the cart drawer's 'open' state.
     */
    handleSectionSelect(event = {}, isSelected = false) {

      /**
       * Ensure that the event is targeting the cart drawer.
       */
      if (event?.detail?.sectionId !== 'cart-drawer') {
        return
      }

      isSelected ?
        mint.EventBus.dispatch('cartDrawer:open') :
        mint.EventBus.dispatch('cartDrawer:close')
    },

    /**
     * Handle opening the cart drawer.
     * - Store the current active element (for keyboard navigation)
     * - Lock the body element
     * - Update state
     */
    open() {
      if (this.state.open) {
        return
      }

      this.lastActiveElement = document.activeElement
      this.state.open = true

      /**
       * Hide the Zendesk chat widget as it would cover the drawer otherwise.
       */
      if (typeof window.zE === 'function') {
        window.zE('webWidget', 'hide')
      }

      /**
       * Wait for others overlays to close before re-adding the `is-locked` class
       */
      nextTick(() => {
        document.body.classList.add(classes.locked)
      })
    },

    /**
     * Set listeners for events from the theme editor and EventBus.
     */
    setEventListeners() {

      /**
       * Shopify section events.
       * - These will open or close the cart drawer when it's selected in the
       *   theme editor.
       */
      document.addEventListener('shopify:section:select', (event) => {
        this.handleSectionSelect(event, true)
      })

      document.addEventListener('shopify:section:deselect', (event) => {
        this.handleSectionSelect(event, false)
      })

      /**
       * EventBus handlers for open, close, and toggle events.
       */
      mint.EventBus.on('cartDrawer:open', () => {
        this.open()
      })

      mint.EventBus.on('cartDrawer:close', () => {
        this.close()
      })

      /**
       * On toggle, dispatch an open or close event based on current state.
       * - This makes it easier for other components to listen to these events,
       *   as the toggle event does not indicate the current state.
       */
      mint.EventBus.on('cartDrawer:toggle', () => {
        const event = this.state.open ? 'close' : 'open'
        mint.EventBus.dispatch(`cartDrawer:${event}`)
      })

      /**
       * EventBus handler for cart updates.
       */
      mint.EventBus.on('mint:cart:updated', (cart) => {
        this.state.locked = false

        if (
          cart?.request?.event === 'cart:added' &&
          !cart?.request?.preventDrawer
        ) {
          mint.EventBus.dispatch('cartDrawer:open')
        }
      })
    },
  },
}
</script>

<style src="./cart-drawer.scss" lang="scss"></style>
